import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, ChevronLeft, ChevronRight, Save, Eye, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import api from "@/lib/api";
import StepCustomer from "@/components/quote/StepCustomer";
import StepHome from "@/components/quote/StepHome";
import StepSite from "@/components/quote/StepSite";
import StepPricing from "@/components/quote/StepPricing";
import StepFinancing from "@/components/quote/StepFinancing";
import StepNotes from "@/components/quote/StepNotes";
import StepReview from "@/components/quote/StepReview";
import TotalsPanel from "@/components/quote/TotalsPanel";
import { isManagerOrAdmin, useAuth } from "@/lib/auth";

const STEPS = [
    { key: "customer", title: "Customer", description: "Who is this quote for?" },
    { key: "home", title: "Home", description: "Choose the manufactured home" },
    { key: "site", title: "Site & Delivery", description: "Where is it going?" },
    { key: "pricing", title: "Pricing", description: "Line items, services, add-ons" },
    { key: "financing", title: "Financing", description: "Payment & deposits" },
    { key: "notes", title: "Notes", description: "Customer & internal notes" },
    { key: "review", title: "Review", description: "Generate & send" },
];

const EMPTY_QUOTE = {
    quote_date: new Date().toISOString().slice(0, 10),
    customer_id: null,
    home: null,
    site: { wind_zone_required: false, wind_zone_desired: false },
    base_price: 0,
    factory_cost: 0,
    options: [],
    mandatory_services: [],
    suppressed_required_service_ids: [],
    addons: [],
    site_work: [],
    discounts: [],
    deposits: [],
    sales_tax_rate: null,
    ehs_loan_used: false,
    financing: {
        purchase_type: "financing",
        financing_status: "not_applicable",
        ehs_loan_used: false,
        notes_internal: "",
        notes_customer: "",
    },
    timeline: {},
    notes_internal: "",
    notes_customer: "",
    next_steps: "",
    disclaimer: "",
    status: "draft",
};

const ORIGIN_PROFILES = [
    { match: ["cavco ocala", "cavco"], manufacturer_id: "CAV-OCA", company_name: "Cavco Ocala", factory_address: "931 NW 37th Ave, Ocala, FL 34475", factory_to_dealer_miles: 50, home_origin: "florida", route: "factory_to_customer" },
    { match: ["palm harbor", "cavco plant city", "plant city"], manufacturer_id: "CAV-PLA", company_name: "Cavco Plant City", factory_address: "605 S. Frontage Road, Plant City, FL 33563", factory_to_dealer_miles: 50, home_origin: "florida", route: "factory_to_customer" },
    { match: ["champion", "lake city"], manufacturer_id: "CHA-LAK", company_name: "Champion Lake City", factory_address: "1915 SE State Rd 100, Lake City, FL 32025", factory_to_dealer_miles: 130, home_origin: "florida", route: "factory_to_customer" },
    { match: ["clayton addison", "addison", "tempo"], manufacturer_id: "CLA-ADD", company_name: "Clayton Addison", factory_address: "17921 Co Rd 41, Addison, AL 35540", factory_to_dealer_miles: 580, home_origin: "outside_florida", route: "factory_to_customer" },
    { match: ["clayton russellville", "russellville", "southern", "se homes"], manufacturer_id: "CLA-RUS", company_name: "Clayton Russellville", factory_address: "1037 Hwy 44, Russellville, AL 35654", factory_to_dealer_miles: 620, home_origin: "outside_florida", route: "factory_to_customer" },
    { match: ["clayton tru", "tru", "lynn"], manufacturer_id: "CLA-TRU", company_name: "Clayton Tru", factory_address: "2746 County Road 59, Lynn, AL 35575", factory_to_dealer_miles: 600, home_origin: "outside_florida", route: "factory_to_customer" },
    { match: ["legacy", "eatonton"], manufacturer_id: "LEG-EAT", company_name: "Legacy Eatonton", factory_address: "184 Industrial Blvd, Eatonton, GA 31024", factory_to_dealer_miles: 410, home_origin: "outside_florida", route: "factory_to_customer" },
    { match: ["skyline", "ocala"], manufacturer_id: "SKY-OCA", company_name: "Skyline Ocala", factory_address: "1230 SW 10th St, Ocala, FL 34471", factory_to_dealer_miles: 50, home_origin: "florida", route: "factory_to_customer" },
    { match: ["timber creek", "timberland", "bear creek"], manufacturer_id: "TIM-BEA", company_name: "Timber Creek", factory_address: "674 Co Rd 65, Bear Creek, AL 35543", factory_to_dealer_miles: 600, home_origin: "outside_florida", route: "factory_to_customer" },
];

const HOME_PACKET_FIELDS = [
    "manufacturer_full",
    "manufacturer_id",
    "description",
    "short_description",
    "summary",
    "spec_summary",
    "image",
    "image_url",
    "primary_image_url",
    "photo_url",
    "exterior_image_url",
    "floorplan_url",
    "floor_plan_url",
    "floorplan_image_url",
    "plan_image_url",
];

const HISTORICAL_PRICING_MESSAGE = "Historical quote pricing fields require migrate_pricing=true";
const HISTORICAL_PRICING_TOAST = "This older quote uses preserved pricing. Pricing changes require creating a new repriced quote or an authorized migration.";

const SAVE_FIELDS = Object.keys(EMPTY_QUOTE).concat(["ehs_price_override_reason", "reset_ehs_price_override"]);
function stableStringify(value) {
    if (Array.isArray(value)) return `[${value.map(stableStringify).join(",")}]`;
    if (value && typeof value === "object") return `{${Object.keys(value).sort().map((k) => `${JSON.stringify(k)}:${stableStringify(value[k])}`).join(",")}}`;
    return JSON.stringify(value ?? null);
}
function buildChangedQuotePatch(current = {}, original = {}) {
    return SAVE_FIELDS.reduce((patch, key) => {
        if (stableStringify(current[key]) !== stableStringify(original[key])) patch[key] = current[key];
        return patch;
    }, {});
}

function formatValidationIssue(issue) {
    if (!issue || typeof issue !== "object") return String(issue || "Validation error");
    const path = Array.isArray(issue.loc) ? issue.loc.join(".") : issue.field || issue.path;
    const msg = issue.msg || issue.message || issue.detail || "Invalid value";
    return path ? `${path}: ${msg}` : String(msg);
}

function formatApiError(err) {
    if (!err?.response) {
        if (err?.request || err?.code === "ERR_NETWORK") return "Network error. Please check your connection and try again.";
        return typeof err?.message === "string" && err.message ? err.message : "Something went wrong. Please try again.";
    }

    const data = err.response?.data;
    const detail = data?.detail ?? data?.message ?? data?.error;
    if (detail === HISTORICAL_PRICING_MESSAGE || data?.message === HISTORICAL_PRICING_MESSAGE) return HISTORICAL_PRICING_TOAST;
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map(formatValidationIssue).join("; ") || "Validation error";
    if (detail && typeof detail === "object") {
        const message = detail.message || detail.detail || detail.error || "Save failed";
        const fields = detail.fields && typeof detail.fields === "object"
            ? Object.entries(detail.fields).map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(", ") : String(value)}`)
            : [];
        return [message, ...fields].join("; ");
    }
    return "Save failed";
}

function isHistoricalPricingError(err) {
    const data = err?.response?.data || {};
    const detail = data.detail;
    return err?.response?.status === 400 && (
        data.message === HISTORICAL_PRICING_MESSAGE ||
        detail === HISTORICAL_PRICING_MESSAGE ||
        detail?.message === HISTORICAL_PRICING_MESSAGE
    );
}

function norm(value) {
    return String(value || "").toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();
}

function resolveOriginProfile(home = {}) {
    const haystack = norm([home.manufacturer, home.manufacturer_full, home.series, home.model_name].filter(Boolean).join(" "));
    if (!haystack) return null;
    let best = null;
    let bestScore = 0;
    for (const profile of ORIGIN_PROFILES) {
        let score = 0;
        for (const term of profile.match) {
            const target = norm(term);
            if (target && haystack.includes(target)) score += target.length;
        }
        if (score > bestScore) {
            best = profile;
            bestScore = score;
        }
    }
    return bestScore > 0 ? best : null;
}

function pickHomePacketFields(home = {}) {
    return HOME_PACKET_FIELDS.reduce((fields, key) => {
        if (home[key] !== undefined && home[key] !== null && home[key] !== "") {
            fields[key] = home[key];
        }
        return fields;
    }, {});
}


function buildPricingPreviewInput(quote = {}) {
    return {
        home: quote.home || null,
        site: quote.site || {},
        base_price: quote.base_price || 0,
        factory_cost: quote.factory_cost || 0,
        options: quote.options || [],
        mandatory_services: quote.mandatory_services || [],
        suppressed_required_service_ids: quote.suppressed_required_service_ids || [],
        addons: quote.addons || [],
        site_work: quote.site_work || [],
        discounts: quote.discounts || [],
        deposits: quote.deposits || [],
        sales_tax_rate: quote.sales_tax_rate ?? null,
        ehs_loan_used: !!quote.ehs_loan_used,
        financing: quote.financing || {},
    };
}

function inferHomeTypeFromHome(home = {}) {
    const explicitType = String(home.home_type || "").trim();
    if (explicitType) return { value: explicitType, source: "catalog" };
    const rawWidth = Number(home.width);
    let width = Number.isFinite(rawWidth) && rawWidth > 0 ? rawWidth : null;
    if (!width && home.dimensions) {
        const match = String(home.dimensions).match(/(\d+(?:\.\d+)?)\s*(?:x|×|by)\s*(\d+(?:\.\d+)?)/i);
        if (match) {
            const first = Number(match[1]);
            const second = Number(match[2]);
            const min = Math.min(first, second);
            if (Number.isFinite(min) && min > 0) width = min;
        }
    }
    if (!width) return { value: "", source: "unknown" };
    if (width < 24) return { value: "Single Wide", source: "inferred" };
    if (width <= 36) return { value: "Double Wide", source: "inferred" };
    return { value: "Triple Wide", source: "inferred" };
}

export default function QuoteBuilder() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const canMigrateHistoricalQuote = isManagerOrAdmin(user);
    const [searchParams] = useSearchParams();
    const customerIdFromQuery = searchParams.get("customer_id");
    const [quote, setQuote] = useState(EMPTY_QUOTE);
    const [stepIdx, setStepIdx] = useState(0);
    const [savedId, setSavedId] = useState(id || null);
    const [savingStatus, setSavingStatus] = useState("idle"); // idle | saving | saved
    const [quoteLoading, setQuoteLoading] = useState(!!id);
    const [quoteLoadError, setQuoteLoadError] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [settings, setSettings] = useState(null);
    const [previewTotals, setPreviewTotals] = useState({});
    const [previewMetadata, setPreviewMetadata] = useState({});
    const previewRequestSeq = useRef(0);
    const originalQuoteRef = useRef(EMPTY_QUOTE);
    const lastCalculatedEhsPriceRef = useRef(null);
    const workflowContentRef = useRef(null);
    const [historicalPricingModalOpen, setHistoricalPricingModalOpen] = useState(false);
    const [historicalPricingAction, setHistoricalPricingAction] = useState("idle");

    const loadQuote = useCallback(async () => {
        if (!id) return true;
        setQuoteLoading(true);
        setQuoteLoadError(null);
        try {
            const r = await api.get(`/quotes/${id}`);
            const q = r.data || {};
            const loadedQuote = {
                ...EMPTY_QUOTE,
                ...q,
            };
            setQuote(loadedQuote);
            originalQuoteRef.current = loadedQuote;
            if (q.customer_snapshot) setCustomer(q.customer_snapshot);
            setSavedId(id);
            return true;
        } catch (err) {
            const message = formatApiError(err);
            setQuoteLoadError(message);
            toast.error(message);
            return false;
        } finally {
            setQuoteLoading(false);
        }
    }, [id]);

    useEffect(() => {
        let cancelled = false;

        api.get("/settings").then((r) => {
            if (!cancelled) setSettings(r.data);
        });

        if (id) {
            loadQuote();
            return () => {
                cancelled = true;
            };
        }

        setQuoteLoading(false);
        setQuoteLoadError(null);
        setQuote(EMPTY_QUOTE);
        originalQuoteRef.current = EMPTY_QUOTE;
        setSavedId(null);

        if (customerIdFromQuery) {
            api.get(`/customers/${customerIdFromQuery}`).then((r) => {
                if (cancelled) return;
                const selectedCustomer = r.data;
                setCustomer(selectedCustomer);
                setQuote((prev) => ({
                    ...prev,
                    customer_id: selectedCustomer?.id || customerIdFromQuery,
                }));
                setStepIdx(1);
            }).catch((err) => toast.error(formatApiError(err)));
        } else {
            setCustomer(null);
            setStepIdx(0);
        }

        return () => {
            cancelled = true;
        };
    }, [id, customerIdFromQuery, loadQuote]);

    const pricingPreviewInput = useMemo(() => buildPricingPreviewInput(quote), [
        quote.home,
        quote.site,
        quote.base_price,
        quote.factory_cost,
        quote.options,
        quote.mandatory_services,
        quote.suppressed_required_service_ids,
        quote.addons,
        quote.site_work,
        quote.discounts,
        quote.deposits,
        quote.sales_tax_rate,
        quote.ehs_loan_used,
        quote.financing,
    ]);

    const debounceRef = useRef(null);
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        const controller = new AbortController();
        const requestSeq = previewRequestSeq.current + 1;
        previewRequestSeq.current = requestSeq;

        debounceRef.current = setTimeout(() => {
            api.post("/pricing/preview", pricingPreviewInput, { signal: controller.signal }).then((r) => {
                if (controller.signal.aborted || requestSeq !== previewRequestSeq.current) return;
                const data = r.data || {};
                const quoteUpdates = data.quote_updates || {};
                setPreviewTotals(data.totals || quoteUpdates.totals || data);
                setPreviewMetadata({
                    pricing_engine_version: quoteUpdates.pricing_engine_version,
                    pricing_version: quoteUpdates.pricing_version,
                    pricing_engine_calculated_at: quoteUpdates.pricing_engine_calculated_at,
                    calculated_at: quoteUpdates.calculated_at,
                    calculation_snapshot: quoteUpdates.calculation_snapshot,
                    validation: quoteUpdates.validation,
                });
                const calculatedBasePrice = Number((data.totals || quoteUpdates.totals || {}).ehs_price_calculated || 0);
                const previousCalculatedBasePrice = lastCalculatedEhsPriceRef.current;
                lastCalculatedEhsPriceRef.current = calculatedBasePrice || null;
                if (calculatedBasePrice > 0 && !quote.ehs_price_override?.active && !quote.ehs_price_override_reason) {
                    const currentBasePrice = Number(quote.base_price || 0);
                    const originalBasePrice = Number(originalQuoteRef.current?.base_price || 0);
                    const shouldReconcileBasePrice = !currentBasePrice || currentBasePrice === originalBasePrice || currentBasePrice === Number(previousCalculatedBasePrice || 0);
                    if (shouldReconcileBasePrice && currentBasePrice !== calculatedBasePrice) {
                        setQuote((prev) => ({ ...prev, base_price: calculatedBasePrice }));
                    }
                }
            }).catch((err) => {
                if (err?.name === "CanceledError" || err?.code === "ERR_CANCELED" || controller.signal.aborted) return;
            });
        }, 300);
        return () => {
            controller.abort();
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [pricingPreviewInput]);

    const update = useCallback((patch) => {
        setQuote((prev) => ({ ...prev, ...(typeof patch === "function" ? patch(prev) : patch) }));
    }, []);

    const saveDraft = useCallback(async (silent = false) => {
        setSavingStatus("saving");
        try {
            let res;
            if (savedId) {
                res = await api.patch(`/quotes/${savedId}`, buildChangedQuotePatch(quote, originalQuoteRef.current));
            } else {
                res = await api.post("/quotes", quote);
            }
            const updated = res.data;
            if (!savedId) setSavedId(updated.id);
            const nextQuote = { ...quote, ...updated, quote_updates: undefined };
            originalQuoteRef.current = nextQuote;
            setQuote(nextQuote);
            setPreviewTotals(updated.totals || {});
            setSavingStatus("saved");
            if (!silent) toast.success(savedId ? "Quote updated" : "Quote saved as draft");
            setTimeout(() => setSavingStatus("idle"), 1200);
            return updated;
        } catch (err) {
            setSavingStatus("idle");
            if (isHistoricalPricingError(err)) setHistoricalPricingModalOpen(true);
            toast.error(formatApiError(err));
            return null;
        }
    }, [quote, savedId]);

    const duplicateAndReprice = async () => {
        if (!savedId) return;
        setHistoricalPricingAction("duplicating");
        try {
            const res = await api.post(`/quotes/${savedId}/duplicate-and-reprice`, quote);
            const duplicate = res.data || {};
            if (duplicate.id) {
                setSavedId(duplicate.id);
                setQuote((prev) => ({ ...prev, ...duplicate, quote_updates: undefined }));
                setPreviewTotals(duplicate.totals || {});
                setHistoricalPricingModalOpen(false);
                toast.success("Duplicated quote for current pricing");
                navigate(`/quotes/${duplicate.id}/edit`);
            }
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setHistoricalPricingAction("idle");
        }
    };

    const migrateCurrentQuote = async () => {
        setHistoricalPricingAction("migrating");
        try {
            const res = await api.patch(`/quotes/${savedId}`, { ...quote, migrate_pricing: true });
            const updated = res.data || {};
            setQuote((prev) => ({ ...prev, ...updated, quote_updates: undefined }));
            setPreviewTotals(updated.totals || {});
            setHistoricalPricingModalOpen(false);
            toast.success("Historical quote migrated to current pricing");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setHistoricalPricingAction("idle");
        }
    };

    const goNext = async () => {
        if (stepIdx < STEPS.length - 1) {
            const saved = await saveDraft(true);
            if (!saved) return;
            setStepIdx((i) => i + 1);
        }
    };
    const goPrev = () => stepIdx > 0 && setStepIdx((i) => i - 1);

    const onSelectCustomer = (cust) => {
        setCustomer(cust);
        update({ customer_id: cust?.id || null });
    };

    const onSelectHome = (home) => {
        if (!home) {
            update({ home: null, base_price: 0, factory_cost: 0 });
            return;
        }
        const resolvedHomeType = inferHomeTypeFromHome(home);
        const origin = resolveOriginProfile(home);
        update((prev) => ({
            home: {
                home_id: home.id,
                manufacturer: home.manufacturer,
                series: home.series,
                model_name: home.model_name,
                home_type: resolvedHomeType.value || undefined,
                home_type_source: resolvedHomeType.source,
                floors: home.floors,
                beds: home.beds,
                baths: home.baths,
                sqft: home.sqft,
                dimensions: home.dimensions,
                width: home.width,
                length: home.length,
                wind_zone: home.wind_zone,
                ...pickHomePacketFields(home),
            },
            site: origin ? {
                ...(prev.site || {}),
                delivery_route_type: origin.route,
                factory_address: origin.factory_address,
                factory_to_dealer_miles: origin.factory_to_dealer_miles,
                home_origin: origin.home_origin,
                manufacturer_origin_id: origin.manufacturer_id,
                manufacturer_origin_name: origin.company_name,
                manufacturer_origin_status: "v05_profile_match",
            } : {
                ...(prev.site || {}),
                manufacturer_origin_status: "needs_origin_verification",
            },
            base_price: home.ehs_price || 0,
            factory_cost: home.est_factory_cost || 0,
        }));
    };

    const currentStep = STEPS[stepIdx];

    useEffect(() => {
        if (!workflowContentRef.current) return;
        workflowContentRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }, [stepIdx]);

    if (quoteLoading) {
        return (
            <div className="mx-auto w-full max-w-[1180px] px-4 pb-10" data-testid="quote-builder-loading">
                <Card className="rounded-xl border-slate-200 p-6">
                    <div className="text-xs uppercase tracking-wider text-slate-500">Loading quote</div>
                    <h1 className="mt-2 font-display text-2xl text-slate-900">Opening quote…</h1>
                    <p className="mt-2 text-sm text-slate-600">Please wait while we load the quote details.</p>
                </Card>
            </div>
        );
    }

    if (quoteLoadError) {
        return (
            <div className="mx-auto w-full max-w-[760px] px-4 pb-10" data-testid="quote-builder-load-error">
                <Card className="space-y-4 rounded-xl border-red-200 bg-red-50 p-6">
                    <div>
                        <div className="text-xs uppercase tracking-wider text-red-600">Could not load quote</div>
                        <h1 className="mt-2 font-display text-2xl text-slate-900">We could not open this quote.</h1>
                        <p className="mt-2 text-sm text-slate-700" data-testid="quote-builder-load-error-message">{quoteLoadError}</p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                        <Button onClick={loadQuote} data-testid="quote-builder-retry-load-button">Retry</Button>
                        <Button variant="outline" onClick={() => navigate("/quotes")} data-testid="quote-builder-back-to-quotes-button">Back to Quotes</Button>
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="mx-auto w-full max-w-[1180px] min-w-0 space-y-6 overflow-x-hidden px-4 pb-10" data-testid="quote-builder-page">
            {historicalPricingModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4" role="dialog" aria-modal="true" aria-labelledby="historical-pricing-title" data-testid="historical-pricing-modal">
                    <Card className="w-full max-w-lg space-y-4 rounded-xl bg-white p-6 shadow-xl">
                        <div>
                            <h2 id="historical-pricing-title" className="text-lg font-semibold text-slate-900">Historical pricing needs a new workflow</h2>
                            <p className="mt-2 text-sm text-slate-600">{HISTORICAL_PRICING_TOAST}</p>
                        </div>
                        <div className="flex flex-wrap justify-end gap-2">
                            <Button variant="outline" onClick={() => setHistoricalPricingModalOpen(false)} data-testid="historical-pricing-cancel-button">Cancel</Button>
                            {canMigrateHistoricalQuote && (
                                <Button variant="outline" onClick={migrateCurrentQuote} disabled={historicalPricingAction !== "idle"} data-testid="historical-pricing-migrate-button">Migrate Current Quote</Button>
                            )}
                            <Button onClick={duplicateAndReprice} disabled={historicalPricingAction !== "idle"} className="bg-[#0F2A47] hover:bg-[#14385E] text-white" data-testid="historical-pricing-duplicate-button">Duplicate & Reprice as New Quote</Button>
                        </div>
                    </Card>
                </div>
            )}
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <Link
                        to="/quotes"
                        className="text-xs text-slate-500 hover:text-slate-700 inline-flex items-center gap-1"
                        data-testid="quote-builder-back-link"
                    >
                        <ArrowLeft className="h-3 w-3" /> All quotes
                    </Link>
                    <div className="text-xs uppercase tracking-wider text-slate-500 mt-1">
                        {savedId ? "Edit Quote" : "New Quote"}
                    </div>
                    <h1 className="font-display text-3xl text-slate-900">
                        {customer ? `${customer.first_name} ${customer.last_name}` : "Build a polished customer quote"}
                    </h1>
                    {quote.home?.model_name && (
                        <p className="text-sm text-slate-500 mt-1">
                            {quote.home?.manufacturer} · {quote.home?.series} · {quote.home?.model_name}
                        </p>
                    )}
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-slate-500" data-testid="quote-builder-save-status">
                        {savingStatus === "saving" && "Saving…"}
                        {savingStatus === "saved" && "All changes saved"}
                    </span>
                    <Button variant="outline" onClick={() => saveDraft()} data-testid="quote-builder-save-draft-button">
                        <Save className="mr-2 h-4 w-4" /> Save draft
                    </Button>
                    {savedId && (
                        <Button variant="outline" onClick={() => navigate(`/quotes/${savedId}`)} data-testid="quote-builder-view-quote-button">
                            <Eye className="mr-2 h-4 w-4" /> Preview
                        </Button>
                    )}
                </div>
            </div>

            <div className="grid min-w-0 grid-cols-1 gap-6 2xl:grid-cols-[260px_minmax(0,1fr)_300px]">
                <Card className="min-w-0 overflow-hidden rounded-xl border-slate-200 p-3 h-fit 2xl:sticky 2xl:top-6">
                    <div className="px-2 py-2 border-b border-slate-100 mb-2">
                        <div className="text-xs uppercase tracking-wider text-slate-500">Workflow</div>
                        <div className="text-sm font-medium text-slate-700">
                            Step {stepIdx + 1} of {STEPS.length}
                        </div>
                    </div>
                    <ol className="space-y-1">
                        {STEPS.map((s, i) => {
                            const done = i < stepIdx;
                            const active = i === stepIdx;
                            return (
                                <li key={s.key}>
                                    <button
                                        onClick={() => setStepIdx(i)}
                                        className={`w-full text-left flex items-start gap-3 rounded-lg px-3 py-2 transition-colors duration-150 ${
                                            active
                                                ? "bg-[rgba(67,161,215,0.10)] border border-[rgba(67,161,215,0.18)]"
                                                : "hover:bg-slate-50"
                                        }`}
                                        data-testid={`quote-builder-step-${s.key}`}
                                    >
                                        <span
                                            className={`mt-0.5 inline-flex h-6 w-6 items-center justify-center rounded-full border text-xs font-medium ${
                                                done
                                                    ? "border-[rgba(22,122,74,0.35)] bg-[rgba(22,122,74,0.10)] text-[#167A4A]"
                                                    : active
                                                    ? "border-[rgba(67,161,215,0.45)] bg-[rgba(67,161,215,0.12)] text-[#1E6FA8]"
                                                    : "border-slate-200 bg-white text-slate-500"
                                            }`}
                                        >
                                            {done ? <CheckCircle2 className="h-3.5 w-3.5" /> : i + 1}
                                        </span>
                                        <span>
                                            <span className="block text-sm font-medium text-slate-800">{s.title}</span>
                                            <span className="block text-xs text-slate-500">{s.description}</span>
                                        </span>
                                    </button>
                                </li>
                            );
                        })}
                    </ol>
                </Card>

                <div className="min-w-0" ref={workflowContentRef}>
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={currentStep.key}
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -4 }}
                            transition={{ duration: 0.18 }}
                        >
                            {currentStep.key === "customer" && (
                                <StepCustomer customer={customer} onSelect={onSelectCustomer} />
                            )}
                            {currentStep.key === "home" && (
                                <StepHome home={quote.home} onSelect={onSelectHome} basePrice={quote.base_price} factoryCost={quote.factory_cost} calculatedEhsPrice={previewTotals.ehs_price_calculated} msrpCalculated={previewTotals.msrp_calculated} priceOverridden={!!quote.ehs_price_override?.active} overrideReason={quote.ehs_price_override_reason} update={update} />
                            )}
                            {currentStep.key === "site" && (
                                <StepSite quote={quote} update={update} customer={customer} />
                            )}
                            {currentStep.key === "pricing" && (
                                <StepPricing quote={quote} update={update} settings={settings} />
                            )}
                            {currentStep.key === "financing" && (
                                <StepFinancing quote={quote} update={update} />
                            )}
                            {currentStep.key === "notes" && (
                                <StepNotes quote={quote} update={update} settings={settings} />
                            )}
                            {currentStep.key === "review" && (
                                <StepReview
                                    quote={quote}
                                    customer={customer}
                                    savedId={savedId}
                                    onSave={saveDraft}
                                    settings={settings}
                                    update={update}
                                    previewTotals={previewTotals}
                                />
                            )}
                        </motion.div>
                    </AnimatePresence>

                    <div className="mt-6 flex items-center justify-between">
                        <Button variant="outline" onClick={goPrev} disabled={stepIdx === 0} data-testid="quote-builder-prev-step-button">
                            <ChevronLeft className="mr-1 h-4 w-4" /> Back
                        </Button>
                        {stepIdx < STEPS.length - 1 ? (
                            <Button onClick={goNext} className="bg-[#0F2A47] hover:bg-[#14385E] text-white" data-testid="quote-builder-next-step-button">
                                Continue <ChevronRight className="ml-1 h-4 w-4" />
                            </Button>
                        ) : (
                            <Button
                                onClick={async () => {
                                    const updated = await saveDraft();
                                    if (updated) navigate(`/quotes/${updated.id || savedId}`);
                                }}
                                className="bg-[#0F2A47] hover:bg-[#14385E] text-white"
                                data-testid="quote-builder-finish-button"
                            >
                                <CheckCircle2 className="mr-1 h-4 w-4" /> Finish & view
                            </Button>
                        )}
                    </div>
                </div>

                <div className="min-w-0 h-fit 2xl:sticky 2xl:top-6">
                    <div className="sr-only" data-testid="quote-builder-preview-metadata">
                        {previewMetadata.pricing_engine_version || previewMetadata.pricing_version || "preview"}
                    </div>
                    <TotalsPanel totals={previewTotals} />
                </div>
            </div>
        </div>
    );
}
